import type { ScatterPoint, SprintScopeChangeData } from '../../../common/types';
import { type SprintEvent, getTimestamp } from '../../../common/utils/sprint-event';

// copied from the deprecated @atlassian/jira-board package
// https://design.atlassian.com/brand/colors/
const colors = {
	navy: '#205081',
	cyan: '#59afe1',
	white: '#ffffff',
	lightGray: '#f5f5f5',
	charcoal: '#333333',
	green: '#14892c',
	midGreen: '#67ab49',
	yellow: '#f6c342',
	red: '#d04437',
	cheetoOrange: '#f79232',
	blue: '#3572b0',
	coolBlue: '#84bbc6',
	violet: '#654982',
	mediumGray: '#707070',
	gray: '#999999',
	ashGray: '#cccccc',
	silver: '#e9e9e9',

	// other
	// used for selected state in jira software
	otherSoftBlue: '#ebf2f9',
	paleBlue: '#ccd9ea',
	otherSoftGreen: '#f3f9f4',

	flashYellow: 'rgba(255, 255, 153, 0.8)',
	shadow: 'rgba(0, 0, 0, 0.2)',
} as const;

export const hasDeltaScope = ({ prevScope, scope }: SprintScopeChangeData) =>
	Math.abs(scope - prevScope) > 0;

export const hasDeltaCompletion = ({ prevCompletion, completion }: SprintScopeChangeData) =>
	Math.abs(prevCompletion - completion) > 0;

/**
 * Events will show on the chart as scatter points if they're:
 *
 * - The start/end events
 * - They had some effect on the line
 */
export const eventShouldHaveScatterPoint = (
	event: SprintEvent,
	hasDelta: (arg1: SprintScopeChangeData) => boolean,
): boolean =>
	event.type === 'start' ||
	event.type === 'end' ||
	(event.type === 'scope' && hasDelta(event.data));

/**
 * Converts event to echarts scatter point with meta. We don't type it because this is only passed
 * into the library and then retrieved, with no type.
 */
export const sprintEventToScatterPoint = (
	event: SprintEvent,
	valueResolver: (arg1: SprintEvent) => number,
): ScatterPoint => ({
	value: [getTimestamp(event), valueResolver(event)],
	meta: { event },
});

/**
 * Generate ECharts series for the events' scatter dots
 */
const getScatterSeries = (
	isMouseOver: boolean,
	allEvents: SprintEvent[],
	seriesName: string,
	color: string,
	valueResolver: (arg1: SprintEvent) => number,
	hasDelta: (arg1: SprintScopeChangeData) => boolean, // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => ({
	type: 'scatter',
	symbolSize: !isMouseOver ? 0 : 8,
	cursor: 'initial',
	z: 3,
	name: seriesName,
	itemStyle: {
		color: colors.white,
		borderWidth: 2,
		borderColor: color,
	},
	// The line is for any event that actually had a delta in the remaining or start/end events.
	// We enrich the points with themselves so we can get the event when rendering the tooltip.
	data: allEvents
		.filter((event) => eventShouldHaveScatterPoint(event, hasDelta))
		.map((event) => sprintEventToScatterPoint(event, valueResolver)),
});

export default getScatterSeries;
